import { DotFilledIcon } from '@radix-ui/react-icons';
import { Flex, SegmentedControl, Text } from '@radix-ui/themes';
import { WebSocketHelper } from 'classes/helpers/web-socket.helper';
import { useMachineMissedDetectionsDialogState } from 'components/sections/admin-portal/machines/hooks/useMachineMissedDetectionsDialogState';
import { useMachineShotHistoryDialogState } from 'components/sections/admin-portal/machines/hooks/useMachineShotHistoryDialogState';
import { HistoryTab } from 'enums/machine-settings';
import { StringHelper } from 'lib_ts/classes/string.helper';
import { WsMsgType } from 'lib_ts/enums/machine-msg.enum';
import { RADIX } from 'lib_ts/enums/radix-ui';
import { IMachineDetailsShotMsg } from 'lib_ts/interfaces/machine-msg/i-machine-details';
import { useEffect, useState } from 'react';

interface INewRowCounter {
  numNewMissedDetections: number;
  numNewShots: number;
}

export const HistoryTabSelector = () => {
  const { historyTab, shotID, setHistoryTab } =
    useMachineShotHistoryDialogState();
  const { missedDetectionID } = useMachineMissedDetectionsDialogState();
  const [highlightKey, setHighlightKey] = useState<number>(Date.now());

  // todo: turn the dot into an actual counter
  const [newRowCounter, setNewRowCounter] = useState<INewRowCounter>({
    numNewMissedDetections: 0,
    numNewShots: 0,
  });

  const activeTab = historyTab ?? HistoryTab.ShotHistory;

  // show new row indicators if we got new rows for the inactive tab
  // e.g. If we're looking at shot history and we got a missed detection, show a counter/indicator on the missed detection tab
  useEffect(() => {
    const incrementNewRowCounter = (event: CustomEvent) => {
      const data = event.detail as IMachineDetailsShotMsg;

      if (data.operation === 'update') {
        return;
      }

      // re-runs css animation without having to remove/add the class
      setHighlightKey(Date.now());

      if (data.missedDetection && activeTab === HistoryTab.ShotHistory) {
        setNewRowCounter((prev) => ({
          ...prev,
          numNewMissedDetections: prev.numNewMissedDetections + 1,
        }));
        return;
      }

      if (data.shot && activeTab === HistoryTab.MissedDetections) {
        setNewRowCounter((prev) => ({
          ...prev,
          numNewShots: prev.numNewShots + 1,
        }));
        return;
      }
    };

    WebSocketHelper.on(
      WsMsgType.S2U_MachineShotHistory,
      incrementNewRowCounter
    );

    return () => {
      WebSocketHelper.remove(
        WsMsgType.S2U_MachineShotHistory,
        incrementNewRowCounter
      );
    };
  }, [activeTab]);

  useEffect(() => {
    if (historyTab === HistoryTab.ShotHistory) {
      setNewRowCounter((prev) => ({ ...prev, numNewShots: 0 }));
      return;
    }

    setNewRowCounter((prev) => ({ ...prev, numNewMissedDetections: 0 }));
  }, [activeTab]);

  const showingDetails = !!shotID || !!missedDetectionID;
  const showShotDot = newRowCounter.numNewShots > 0 && !showingDetails;
  const showMissedDetectionDot =
    newRowCounter.numNewMissedDetections > 0 && !showingDetails;

  return (
    <SegmentedControl.Root
      value={activeTab}
      onValueChange={(v) => {
        setHistoryTab(v as HistoryTab);
      }}
      size="1"
      key={highlightKey}
    >
      <SegmentedControl.Item
        value={HistoryTab.ShotHistory}
        className={StringHelper.classNames([
          'cursor-pointer',
          // todo: make a different highlight css class name for clarity
          showShotDot ? 'table-new-row' : '',
        ])}
      >
        <Flex align="center" gap="1">
          {/* just to ensure equal spacing */}
          <Text color={RADIX.COLOR.ACCENT} style={{ visibility: 'hidden' }}>
            <DotFilledIcon />
          </Text>
          Shot History
          <Text
            color={RADIX.COLOR.ACCENT}
            style={{
              visibility: showShotDot ? 'visible' : 'hidden',
            }}
          >
            <Flex align="center">
              <DotFilledIcon />
            </Flex>
          </Text>
        </Flex>
      </SegmentedControl.Item>
      <SegmentedControl.Item
        value={HistoryTab.MissedDetections}
        className={StringHelper.classNames([
          'cursor-pointer',
          // todo: make a different highlight css class name for clarity
          showMissedDetectionDot ? 'table-new-row' : '',
        ])}
      >
        <Flex align="center" gap="1">
          {/* just to ensure equal spacing */}
          <Text
            color={RADIX.COLOR.ACCENT}
            style={{
              visibility: 'hidden',
            }}
          >
            <DotFilledIcon />
          </Text>
          Missed Detections
          <Text
            color={RADIX.COLOR.ACCENT}
            style={{
              visibility: showMissedDetectionDot ? 'visible' : 'hidden',
            }}
          >
            <Flex align="center">
              <DotFilledIcon />
            </Flex>
          </Text>
        </Flex>
      </SegmentedControl.Item>
    </SegmentedControl.Root>
  );
};
